import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components/root";
import { useImageCaptionModalForm } from "./useImageCaptionModalForm";
import { MediaImage, Sparks } from "iconoir-react";
import { ControlledTextArea } from "shared/components/formControl";
import { FormProvider } from "react-hook-form";
import {
  EnumDKeyAssetType,
  type AssetFile,
  type AssetImage,
  useImageAnalyzeMutation,
} from "gql/generated";
import { Button, Icon, useToastContext } from "@ttc3k/trekker";
import { usePostHog } from "shared/hooks";
import { getMultilingualString } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";

type ImageMediaCaptionModalProps = {
  isOpen: boolean;
  assetId: string;
  assetType: EnumDKeyAssetType;
  caption: string;
  onClose: () => void;
  onCaptionUpdated: (asset: Partial<AssetFile> | Partial<AssetImage>) => void;
};

export const ImageMediaCaptionModal = ({
  isOpen,
  assetId,
  assetType,
  caption,
  onClose,
  onCaptionUpdated,
}: ImageMediaCaptionModalProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const { form, onSubmit, defaultValues } = useImageCaptionModalForm({
    assetId,
    assetType,
    defaultCaption: caption,
    onSuccess: (asset: Partial<AssetFile> | Partial<AssetImage>) => {
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("core:IMAGE.CAPTION.SAVED"),
      });
      form.reset(defaultValues);
      onCaptionUpdated(asset);
      onClose();
    },
    onError: (error: string) => {
      trackPostHogException(new Error("Error updating image caption"), {
        fileName: "ImageMediaCaptionModal.tsx",
        method: "onError",
        message: "Error updating image caption",
      });
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: error,
      });
    },
  });

  const [imageAnalyze, { loading: isAnalyzing }] =
    useImageAnalyzeMutation();

  const handleGenerateAiCaption = async () => {
    try {
      const { data } = await imageAnalyze({
        variables: { assetId },
      });
      const aiCaption = getMultilingualString(
        data?.imageAnalyze?.caption ?? {},
      );
      if (aiCaption) {
        form.setValue("caption", aiCaption, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    } catch (error) {
      trackPostHogException(error, {
        fileName: "ImageMediaCaptionModal.tsx",
        method: "handleGenerateAiCaption",
        message: "Error generating AI caption",
      });
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:IMAGE.CAPTION.GENERATE_AI_ERROR"),
      });
    }
  };

  const advancedImageControls = useFeatureFlag(FeatureFlags.AdvancedImageControls);
  const formId = "image-caption-form";
  const isImageAsset = assetType === EnumDKeyAssetType.AssetImage;

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      title={t("core:IMAGE.CAPTION.TITLE")}
      description={t("core:IMAGE.CAPTION.DESCRIPTION")}
      HeaderIcon={MediaImage}
      onClose={onClose}
      cancelButtonProps={{
        onClick: onClose,
      }}
      saveButtonCopy={t("core:IMAGE.CAPTION.CTA_BUTTON")}
      saveButtonProps={{
        visual: "accent.blue",
        form: formId,
        type: "button",
        isLoading: form.formState.isSubmitting,
        disabled: !form.formState.isDirty,
        onClick: () => {
          onSubmit();
        },
      }}
      portal={false}
    >
      <Flex flexDir="column" gap="200">
        {isImageAsset && advancedImageControls && (
          <Flex>
            <Button
              visual="accent.blue"
              size="sm"
              onClick={handleGenerateAiCaption}
              disabled={isAnalyzing}
              isLoading={isAnalyzing}
              type="button"
            >
              <Icon Element={Sparks} />
              {isAnalyzing
                ? t("core:IMAGE.CAPTION.GENERATING_AI")
                : t("core:IMAGE.CAPTION.GENERATE_AI")}
            </Button>
          </Flex>
        )}
        <FormProvider {...form}>
          <form onSubmit={onSubmit} id={formId}>
            <ControlledTextArea
              label={t("core:IMAGE.CAPTION.LABEL")}
              name="caption"
            ></ControlledTextArea>
          </form>
        </FormProvider>
      </Flex>
    </WhereaboutsModal>
  );
};
